import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { createPlan, updatePlan, getPlanById } from '../../../../services/virtualPosService';
import { ArrowLeft, Save } from 'lucide-react';

const VirtualPosPlansForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'MONTO_FIJO',
    amount: '',
    currency: 'UF',
    frequency_type: 'Mensual',
    automatic_renewal: 'T',
    show_in_terminal: 'F',
    trial_days: 0,
    num_charges: 1,
    fixed_amount_day_charge: '0',
    return_url: '',
    shipping_address: 'F',
    is_active: 'T'
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Función para calcular el día de cargo más cercano
  const calculateNearestChargeDay = (contractDate) => {
    const availableDays = ['0', '01', '05', '10', '15', '20', '25', '28', '30'];
    const contractDay = new Date(contractDate).getDate();
    
    // Convertir días disponibles a números para comparación
    const numericDays = availableDays.map(day => parseInt(day));
    
    // Encontrar el día más cercano
    let nearestDay = numericDays[0];
    let minDifference = Math.abs(contractDay - nearestDay);
    
    for (let day of numericDays) {
      const difference = Math.abs(contractDay - day);
      if (difference < minDifference) {
        minDifference = difference;
        nearestDay = day;
      }
    }
    
    // Retornar en formato string con ceros a la izquierda si es necesario
    return nearestDay.toString().padStart(2, '0');
  };

  // Calcular día de cargo automáticamente cuando se selecciona la fecha
  const handleContractDateChange = (date) => {
    if (date) {
      const nearestDay = calculateNearestChargeDay(date);
      setFormData(prev => ({
        ...prev,
        fixed_amount_day_charge: nearestDay
      }));
    }
  };

  useEffect(() => {
    if (isEditing) {
      loadPlan();
    }
  }, [id, isEditing]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const plan = await getPlanById(id);
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        type: plan.type || 'MONTO_FIJO',
        amount: plan.amount || '',
        currency: plan.currency || 'UF',
        frequency_type: plan.frequency_type || 'Mensual',
        automatic_renewal: plan.automatic_renewal || 'T',
        show_in_terminal: plan.show_in_terminal || 'F',
        trial_days: plan.trial_days || 0,
        num_charges: plan.num_charges || 1,
        fixed_amount_day_charge: plan.fixed_amount_day_charge || '0',
        return_url: plan.return_url || '',
        shipping_address: plan.shipping_address || 'F',
        is_active: plan.is_active || 'T'
      });
    } catch (error) {
      console.error('Error al cargar plan:', error);
      alert('Error al cargar el plan');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      alert('Por favor complete el nombre del plan');
      return;
    }

    // Validar monto solo si es MONTO_FIJO
    if (formData.type === 'MONTO_FIJO' && !formData.amount) {
      alert('Por favor complete el monto para planes de monto fijo');
      return;
    }

    try {
      setSaving(true);
      
      const planData = {
        ...formData,
        amount: formData.type === 'MONTO_FIJO' ? parseFloat(formData.amount) : 0,
        trial_days: parseInt(formData.trial_days),
        num_charges: parseInt(formData.num_charges)
      };

      if (isEditing) {
        // Aquí iría la función updatePlan cuando esté disponible
        alert('Funcionalidad de edición pendiente de implementar');
      } else {
        await createPlan(planData);
        alert('Plan creado exitosamente');
        navigate('/payments-admin/virtualpos/planes');
      }
    } catch (error) {
      console.error('Error al guardar plan:', error);
      alert('Error al guardar el plan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando plan...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/payments-admin/virtualpos/planes')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a Planes
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">
          {isEditing ? 'Editar Plan' : 'Crear Nuevo Plan'}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del Plan *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ej: AMBSALUDMENTAL"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Tipo de Plan *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleInputChange('type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTO_FIJO">Monto Fijo</SelectItem>
                    <SelectItem value="MONTO_VARIABLE">Monto Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.type === 'MONTO_FIJO' && (
                <div className="space-y-2">
                  <Label htmlFor="amount">Monto *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="0.00"
                    required={formData.type === 'MONTO_FIJO'}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="currency">Moneda</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleInputChange('currency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UF">Unidad de Fomento (UF)</SelectItem>
                    <SelectItem value="CLP">Pesos Chilenos (CLP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="frequency_type">Frecuencia</Label>
                <Select
                  value={formData.frequency_type}
                  onValueChange={(value) => handleInputChange('frequency_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mensual">Mensual</SelectItem>
                    <SelectItem value="Anual">Anual</SelectItem>
                    <SelectItem value="Semanal">Semanal</SelectItem>
                    <SelectItem value="Diario">Diario</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fixed_amount_day_charge">Día de Cargo</Label>
                <Select
                  value={formData.fixed_amount_day_charge}
                  onValueChange={(value) => handleInputChange('fixed_amount_day_charge', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Día 0 (Inmediato)</SelectItem>
                    <SelectItem value="01">Día 1</SelectItem>
                    <SelectItem value="05">Día 5</SelectItem>
                    <SelectItem value="10">Día 10</SelectItem>
                    <SelectItem value="15">Día 15</SelectItem>
                    <SelectItem value="20">Día 20</SelectItem>
                    <SelectItem value="25">Día 25</SelectItem>
                    <SelectItem value="28">Día 28</SelectItem>
                    <SelectItem value="30">Día 30</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Configuración Avanzada */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="trial_days">Días de Prueba</Label>
                <Input
                  id="trial_days"
                  type="number"
                  min="0"
                  value={formData.trial_days}
                  onChange={(e) => handleInputChange('trial_days', e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="num_charges">Número de Cargos</Label>
                <Input
                  id="num_charges"
                  type="number"
                  min="1"
                  value={formData.num_charges}
                  onChange={(e) => handleInputChange('num_charges', e.target.value)}
                  placeholder="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="automatic_renewal">Renovación Automática</Label>
                <Select
                  value={formData.automatic_renewal}
                  onValueChange={(value) => handleInputChange('automatic_renewal', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T">Sí</SelectItem>
                    <SelectItem value="F">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="show_in_terminal">Mostrar en Terminal</Label>
                <Select
                  value={formData.show_in_terminal}
                  onValueChange={(value) => handleInputChange('show_in_terminal', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T">Sí</SelectItem>
                    <SelectItem value="F">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="shipping_address">Dirección de Envío</Label>
                <Select
                  value={formData.shipping_address}
                  onValueChange={(value) => handleInputChange('shipping_address', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T">Requerida</SelectItem>
                    <SelectItem value="F">No Requerida</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="is_active">Estado</Label>
                <Select
                  value={formData.is_active}
                  onValueChange={(value) => handleInputChange('is_active', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T">Activo</SelectItem>
                    <SelectItem value="F">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* URLs y Descripción */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Ej: Ambulatorio Bienestar y Salud Mental"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="return_url">URL de Retorno</Label>
                <Input
                  id="return_url"
                  type="url"
                  value={formData.return_url}
                  onChange={(e) => handleInputChange('return_url', e.target.value)}
                  placeholder="https://ejemplo.com/return"
                />
              </div>
            </div>

            {/* Calculadora de Día de Cargo */}
            <Card className="bg-blue-50">
              <CardHeader>
                <CardTitle className="text-sm">Calculadora de Día de Cargo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="contract_date">Fecha de Contratación (para calcular día más cercano)</Label>
                  <Input
                    id="contract_date"
                    type="date"
                    onChange={(e) => handleContractDateChange(e.target.value)}
                  />
                  <p className="text-sm text-gray-600">
                    El sistema calculará automáticamente el día de cargo más cercano a la fecha de contratación.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/payments-admin/virtualpos/planes')}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Plan')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VirtualPosPlansForm;